/**
 * Stempeluhr „Kommen / Pause / Gehen" als reine Funktionen.
 * Eine laufende Stempelung ist ein normaler Eintrag mit end = null.
 */
import type { EntryChange } from '../shared/types.js';
import { berlinDate, berlinTime, berlinToUtc } from '../shared/time.js';

export function newId(): string {
  return globalThis.crypto.randomUUID();
}

export function startClock(now: number, activity: string, id = newId()): EntryChange {
  return {
    id,
    date: berlinDate(now),
    start: berlinTime(now),
    end: null,
    pauseMin: 0,
    pauseStart: null,
    activity,
    note: '',
    source: 'clock',
    createdAt: now,
    updatedAt: now,
    deleted: false,
  };
}

export function isPaused(e: EntryChange): boolean {
  return e.end === null && e.pauseStart !== null;
}

/** Pause beginnen oder beenden. Pausenminuten werden auf ganze Minuten gerundet. */
export function togglePause(e: EntryChange, now: number): EntryChange {
  if (e.end !== null) return e;
  if (e.pauseStart === null) return { ...e, pauseStart: now, updatedAt: now };
  const add = Math.max(0, Math.round((now - e.pauseStart) / 60_000));
  return { ...e, pauseStart: null, pauseMin: e.pauseMin + add, updatedAt: now };
}

/**
 * Gehen: setzt das Ende (lokale Uhrzeit). Läuft noch eine Pause, wird sie
 * beendet. Liefert null, wenn weniger als eine Minute gestempelt wurde –
 * dann soll der Eintrag verworfen werden.
 */
export function stopClock(e: EntryChange, now: number): EntryChange | null {
  let x = e;
  if (x.pauseStart !== null) x = togglePause(x, now);
  const end = berlinTime(now);
  if (end === x.start && now - berlinToUtc(x.date, x.start) < 86_400_000) return null;
  return { ...x, end, pauseStart: null, updatedAt: now };
}

/** Bisher gearbeitete Millisekunden (ohne Pausen). */
export function elapsedMs(e: EntryChange, now: number): number {
  const start = berlinToUtc(e.date, e.start);
  const pause = e.pauseMin * 60_000 + (e.pauseStart !== null ? now - e.pauseStart : 0);
  return Math.max(0, now - start - pause);
}

export function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
